// frontend/src/components/client/FlightPlanPDF.jsx

import React from 'react';
import { Page, Text, View, Document, StyleSheet, Font, Image } from '@react-pdf/renderer';

Font.register({
  family: 'Helvetica',
  src: 'https://fonts.gstatic.com/s/roboto/v27/KFOmCnqEu92Fr1Mu4mxP.ttf'
});

const styles = StyleSheet.create({
  page: { padding: 30, backgroundColor: '#ffffff', fontFamily: 'Helvetica' },
  header: {
    flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center',
    marginBottom: 20, borderBottomWidth: 1, borderBottomColor: '#cccccc', paddingBottom: 15
  },
  logo: { width: 80, height: 80, objectFit: 'contain' },
  titleSection: { alignItems: 'flex-end' },
  title: { fontSize: 20, fontWeight: 'bold', color: '#1e3a5f' },
  subtitle: { fontSize: 10, color: '#666666', marginTop: 5 },
  section: { marginBottom: 15, padding: 10, backgroundColor: '#f8f9fa', borderRadius: 5 },
  sectionTitle: {
    fontSize: 12, fontWeight: 'bold', color: '#1e3a5f', marginBottom: 8,
    borderBottomWidth: 1, borderBottomColor: '#cccccc', paddingBottom: 3
  },
  row: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 5 },
  label: { fontSize: 9, color: '#666666' },
  value: { fontSize: 9, fontWeight: 'bold', color: '#333333' },
  routeCard: {
    backgroundColor: '#e8f4f8', padding: 10, borderRadius: 5, marginBottom: 15,
    flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center'
  },
  airportCode: { fontSize: 14, fontWeight: 'bold', color: '#1e3a5f' },
  airportName: { fontSize: 8, color: '#666666' },
  arrow: { fontSize: 16, color: '#1e3a5f' },
  checklistItem: { flexDirection: 'row', alignItems: 'center', marginBottom: 4 },
  checkbox: { width: 10, height: 10, borderWidth: 1, borderColor: '#333333', marginRight: 8 },
  checkedBox: { width: 10, height: 10, backgroundColor: '#22c55e', borderWidth: 1, borderColor: '#22c55e', marginRight: 8 },
  checklistLabel: { fontSize: 8, color: '#333333', flex: 1 },
  footer: { marginTop: 30, paddingTop: 10, borderTopWidth: 1, borderTopColor: '#cccccc', alignItems: 'center' },
  footerText: { fontSize: 7, color: '#999999' },
  badge: { paddingHorizontal: 6, paddingVertical: 2, borderRadius: 4, fontSize: 8, fontWeight: 'bold' },
  badgePending: { backgroundColor: '#eab308', color: '#ffffff' },
  badgeApproved: { backgroundColor: '#22c55e', color: '#ffffff' },
  badgeCompleted: { backgroundColor: '#3b82f6', color: '#ffffff' },
  fuelValue: { fontSize: 16, fontWeight: 'bold', color: '#f97316' },
  windBox: {
    backgroundColor: '#e0f2fe', padding: 8, borderRadius: 5, marginBottom: 10,
    borderLeftWidth: 3, borderLeftColor: '#0284c7'
  },
  windRow: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 3 },
  windLabel: { fontSize: 8, color: '#0369a1' },
  windValue: { fontSize: 8, fontWeight: 'bold', color: '#0369a1' },
  // Alternate IFR box
  alternateBox: {
    backgroundColor: '#eff6ff', padding: 8, borderRadius: 5, marginBottom: 10,
    borderLeftWidth: 3, borderLeftColor: '#3b82f6'
  },
  alternateRow: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 3 },
  alternateLabel: { fontSize: 8, color: '#1d4ed8' },
  alternateValue: { fontSize: 8, fontWeight: 'bold', color: '#1d4ed8' },
  // Exemption 1-2-3
  exemptBox: {
    backgroundColor: '#f0fdf4', padding: 8, borderRadius: 5, marginBottom: 10,
    borderLeftWidth: 3, borderLeftColor: '#22c55e'
  },
  exemptText: { fontSize: 8, color: '#166534' },
  // Warning boxes
  disclaimerBox: {
    backgroundColor: '#fef3c7', padding: 8, borderRadius: 5, marginTop: 10,
    borderLeftWidth: 3, borderLeftColor: '#d97706'
  },
  disclaimerText: { fontSize: 7, color: '#92400e', textAlign: 'center' },
  warningText: { fontSize: 7, color: '#dc2626', textAlign: 'center', marginTop: 5 },
  infoText: { fontSize: 7, color: '#0369a1', textAlign: 'center', marginTop: 3 },
  holdingNote: { fontSize: 8, color: '#7c3aed', fontWeight: 'bold' },
});

const FlightPlanPDF = ({
  flightPlan,
  request,
  aircraft,
  checklist,
  companyLogo,
  companyName,
  windData,
  // Nouveaux props depuis le résultat du calcul
  calculationResult,
}) => {
  const getStatusBadge = (s) => ({ pending: styles.badgePending, approved: styles.badgeApproved, completed: styles.badgeCompleted }[s] || {});
  const getStatusText  = (s) => ({ pending: 'Pending', approved: 'Approved', completed: 'Completed' }[s] || (s?.toUpperCase() || 'N/A'));

  const formatDate = (d) => {
    if (!d) return 'N/A';
    return new Date(d).toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const checklistLabels = {
    preflight_inspection_complete: 'Preflight inspection complete (14 CFR §91.103)',
    documents_on_board: 'Documents on board (AROW)',
    notam_checked: 'NOTAMs checked',
    weather_briefing_complete: 'Weather briefing obtained',
    weight_and_balance_computed: 'Weight & balance computed',
    fuel_requirements_computed: 'Fuel requirements computed (FAA §91.151/§91.169)',
    alternate_airport_identified: 'Alternate airport identified',
    ifr_clearance_obtained: 'IFR clearance obtained',
    flight_plan_filed: 'Flight plan filed'
  };

  const hasWindData     = windData && windData.wind_source === 'NOAA_AWC';
  const isIFR           = flightPlan?.is_ifr || false;
  const alternateExempt = calculationResult?.alternate_exemption_123 || false;
  const alternateIcao   = calculationResult?.alternate_icao_used || flightPlan?.alternate_icao;
  const altDistNm       = calculationResult?.alternate_distance_used_nm || flightPlan?.alternate_distance_nm;
  const holdingMinutes  = calculationResult?.holding_minutes || (isIFR && !alternateExempt ? 45 : 0);

  const tripFuelValue  = hasWindData ? windData.trip_fuel_gallons_with_wind : flightPlan?.calculated_trip_fuel_gallons;
  const totalFuelValue = hasWindData ? windData.total_fuel_required_gallons : flightPlan?.calculated_total_fuel_required_gallons;

  // Alternate fuel info
  const alternateFuelValue  = calculationResult?.alternate_fuel_gallons || flightPlan?.calculated_alternate_fuel_gallons || 0;
  const holdingFuelValue    = calculationResult?.holding_fuel_gallons   || flightPlan?.calculated_holding_fuel_gallons   || 0;
  const weightPenaltyValue  = calculationResult?.weight_penalty_gallons || 0;

  return (
    <Document>
      <Page size="A4" style={styles.page}>

        {/* Header */}
        <View style={styles.header}>
          {companyLogo && <Image src={companyLogo} style={styles.logo} />}
          <View style={styles.titleSection}>
            <Text style={styles.title}>FLIGHT PLAN</Text>
            <Text style={styles.subtitle}>Fuel Request #{request?.id}</Text>
            <Text style={styles.subtitle}>Generated: {formatDate(new Date())}</Text>
          </View>
        </View>

        {/* Status */}
        <View style={{ alignItems: 'center', marginBottom: 15 }}>
          <View style={[styles.badge, getStatusBadge(request?.status)]}>
            <Text style={{ color: '#ffffff' }}>{getStatusText(request?.status)}</Text>
          </View>
        </View>

        {/* Route */}
        <View style={styles.routeCard}>
          <View style={{ alignItems: 'center' }}>
            <Text style={styles.airportCode}>{flightPlan?.departure_icao || '—'}</Text>
            <Text style={styles.airportName}>{flightPlan?.departure_name || 'Departure'}</Text>
          </View>
          <Text style={styles.arrow}>→</Text>
          <View style={{ alignItems: 'center' }}>
            <Text style={styles.airportCode}>{request?.destination_icao || '—'}</Text>
            <Text style={styles.airportName}>{flightPlan?.destination_name || 'Destination'}</Text>
          </View>
        </View>

        {/* Wind Data */}
        {hasWindData && (
          <View style={styles.windBox}>
            <Text style={[styles.windLabel, { fontWeight: 'bold', marginBottom: 5 }]}>🌬️ NOAA Winds Aloft Data</Text>
            <View style={styles.windRow}>
              <Text style={styles.windLabel}>Headwind Component:</Text>
              <Text style={styles.windValue}>{windData.wind_component_kts} kts</Text>
            </View>
            <View style={styles.windRow}>
              <Text style={styles.windLabel}>Effective Groundspeed:</Text>
              <Text style={styles.windValue}>{windData.effective_groundspeed_kts} kts</Text>
            </View>
            <View style={styles.windRow}>
              <Text style={styles.windLabel}>Adjusted Trip Time:</Text>
              <Text style={styles.windValue}>{windData.adjusted_trip_time_hours} hrs</Text>
            </View>
          </View>
        )}

        {/* IFR Alternate Section — affiché seulement si IFR */}
        {isIFR && (
          alternateExempt ? (
            <View style={styles.exemptBox}>
              <Text style={[styles.exemptText, { fontWeight: 'bold', marginBottom: 3 }]}>
                ✅ ALTERNATE NOT REQUIRED — Rule 1-2-3 §91.169(c)(1)
              </Text>
              <Text style={styles.exemptText}>
                Forecast ceiling and visibility meet exemption criteria. No alternate fuel required.
              </Text>
            </View>
          ) : (
            <View style={styles.alternateBox}>
              <Text style={[styles.alternateLabel, { fontWeight: 'bold', marginBottom: 5 }]}>
                🛬 IFR ALTERNATE — §91.169(b)
              </Text>
              <View style={styles.alternateRow}>
                <Text style={styles.alternateLabel}>Alternate Airport:</Text>
                <Text style={styles.alternateValue}>{alternateIcao || 'Not specified (45-min fallback used)'}</Text>
              </View>
              <View style={styles.alternateRow}>
                <Text style={styles.alternateLabel}>Distance to Alternate:</Text>
                <Text style={styles.alternateValue}>
                  {altDistNm ? `${altDistNm} NM (actual)` : 'Estimated (45-min cruise fallback)'}
                </Text>
              </View>
              <View style={styles.alternateRow}>
                <Text style={styles.alternateLabel}>Alternate Fuel:</Text>
                <Text style={styles.alternateValue}>{alternateFuelValue} gal</Text>
              </View>
              <View style={styles.alternateRow}>
                <Text style={styles.alternateLabel}>Holding (post-alternate):</Text>
                <Text style={styles.holdingNote}>{holdingMinutes} min = {holdingFuelValue} gal  §91.169(b)(1)</Text>
              </View>
            </View>
          )
        )}

        {/* Flight Details */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>FLIGHT DETAILS</Text>
          <View style={styles.row}>
            <Text style={styles.label}>Aircraft:</Text>
            <Text style={styles.value}>{aircraft?.tail_number} — {aircraft?.manufacturer} {aircraft?.model}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>Trip Distance:</Text>
            <Text style={styles.value}>{flightPlan?.trip_distance_nm || '—'} NM</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>Flight Rules:</Text>
            <Text style={styles.value}>{isIFR ? 'IFR' : 'VFR'} {flightPlan?.is_night_flight ? '(Night)' : '(Day)'}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>Scheduled Date:</Text>
            <Text style={styles.value}>{formatDate(request?.scheduled_date)}</Text>
          </View>
        </View>

        {/* Fuel Requirements */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>FUEL REQUIREMENTS (FAA 14 CFR)</Text>
          <View style={styles.row}>
            <Text style={styles.label}>Requested:</Text>
            <Text style={styles.value}>{request?.quantity} gallons</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>Trip Fuel{hasWindData ? ' (wind-adjusted)' : ''}:</Text>
            <Text style={styles.value}>
              {tripFuelValue || '—'} gal
              {hasWindData && ` (no wind: ${flightPlan?.calculated_trip_fuel_gallons} gal)`}
            </Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>Legal Reserve ({isIFR ? '45' : flightPlan?.is_night_flight ? '45' : '30'} min):</Text>
            <Text style={styles.value}>{flightPlan?.calculated_reserve_fuel_gallons || '—'} gal</Text>
          </View>
          {alternateFuelValue > 0 && (
            <View style={styles.row}>
              <Text style={styles.label}>
                Alternate Fuel {altDistNm ? `(${altDistNm} NM)` : '(est.)'}:
              </Text>
              <Text style={styles.value}>{alternateFuelValue} gal</Text>
            </View>
          )}
          {holdingFuelValue > 0 && (
            <View style={styles.row}>
              <Text style={styles.label}>Holding Fuel ({holdingMinutes} min §91.169(b)(1)):</Text>
              <Text style={styles.value}>{holdingFuelValue} gal</Text>
            </View>
          )}
          <View style={styles.row}>
            <Text style={styles.label}>Taxi Fuel:</Text>
            <Text style={styles.value}>{flightPlan?.estimated_taxi_fuel_gallons || 0} gal</Text>
          </View>
          {weightPenaltyValue > 0 && (
            <View style={styles.row}>
              <Text style={styles.label}>Weight adjustment:</Text>
              <Text style={styles.value}>{weightPenaltyValue} gal</Text>
            </View>
          )}
          <View style={[styles.row, { marginTop: 5, borderTopWidth: 1, borderTopColor: '#cccccc', paddingTop: 5 }]}>
            <Text style={styles.label}>TOTAL REQUIRED:</Text>
            <Text style={styles.fuelValue}>{totalFuelValue || '—'} gal</Text>
          </View>
          {flightPlan?.fuel_notes && (
            <View style={styles.row}>
              <Text style={styles.label}>Notes:</Text>
              <Text style={styles.value}>{flightPlan?.fuel_notes}</Text>
            </View>
          )}
        </View>

        {/* Passenger & Load */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>PASSENGER & LOAD</Text>
          <View style={styles.row}>
            <Text style={styles.label}>Passengers (incl. pilot):</Text>
            <Text style={styles.value}>{flightPlan?.passenger_count || 1}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>Baggage:</Text>
            <Text style={styles.value}>{flightPlan?.baggage_weight_lbs || 0} lbs</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>Est. Passenger Weight:</Text>
            <Text style={styles.value}>190 lbs/person (FAA standard)</Text>
          </View>
          {weightPenaltyValue > 0 && (
            <View style={styles.row}>
              <Text style={styles.label}>Weight fuel penalty:</Text>
              <Text style={styles.value}>{weightPenaltyValue} gal (+0.5 gal/100 lbs)</Text>
            </View>
          )}
        </View>

        {/* FAA Checklist */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>FAA REQUIRED CHECKS</Text>
          {Object.entries(checklistLabels).map(([key, label]) => (
            <View key={key} style={styles.checklistItem}>
              <View style={checklist?.[key] ? styles.checkedBox : styles.checkbox} />
              <Text style={styles.checklistLabel}>{label}</Text>
            </View>
          ))}
        </View>

        {/* Disclaimer */}
        <View style={styles.disclaimerBox}>
          <Text style={[styles.disclaimerText, { fontWeight: 'bold' }]}>⚠️ FAA COMPLIANCE NOTICE</Text>
          <Text style={styles.disclaimerText}>This document provides PLANNING ESTIMATES only.</Text>
          <Text style={styles.disclaimerText}>• Obtain official weather briefing from FAA-approved source</Text>
          <Text style={styles.disclaimerText}>• Check NOTAMs for entire route</Text>
          <Text style={styles.disclaimerText}>• File flight plan via Leidos (1800wxbrief.com), ForeFlight, or Garmin Pilot</Text>
          <Text style={styles.disclaimerText}>• Verify weight & balance within aircraft limits</Text>
          {isIFR && !alternateExempt && !altDistNm && (
            <Text style={styles.warningText}>
              ⚠️ IFR alternate distance estimated — enter actual alternate for precise fuel calculation
            </Text>
          )}
          {!hasWindData && (
            <Text style={styles.warningText}>
              Without NOAA winds aloft, fuel calculations may underestimate by 15-30%
            </Text>
          )}
          {isIFR && (
            <Text style={styles.infoText}>
              IFR reserve: 45 min · Holding: 45 min post-alternate · §91.169(b)(1)
            </Text>
          )}
        </View>

        {/* Footer */}
        <View style={styles.footer}>
          <Text style={styles.footerText}>This flight plan is an ESTIMATE — Not a substitute for official FAA filing</Text>
          <Text style={styles.footerText}>Generated by aeroTrick FBO Management System</Text>
          <Text style={styles.footerText}>{companyName || 'aeroTrick'} — All rights reserved</Text>
        </View>

      </Page>
    </Document>
  );
};

export default FlightPlanPDF;